import { QueryFunction, QueryKey } from '@tanstack/react-query';

export type ISuperTopupProposedMember = {
  id: string;
  name: string;
  relationship: 'father' | 'mother' | 'son' | 'daughter' | 'self' | 'spouse';
  dateOfBirth: string;
  gender: 'male' | 'female';
};


export enum EPatientRelationship {
  SELF = 'self',
  SPOUSE = 'spouse',
  KID = 'kid',
  PARENT = 'parent',
  SIBLING = 'sibling',
  PARENT_IN_LAW = 'parentInLaw',
  PARENT_IN_LAW_OTHER = 'parent-in-law',
}

export interface IPlan {
  provider: {
    handle: string;
    name: string;
  };
  type: string;
  category: string;
  tpaName: string;
  label: string;
  faq: {
    description: Array<{
      uiorder: number;
      answer: string;
      question: string;
    }>;
    url: null;
  };
  redeemConfig: null;
  costType: string;
  networkHospitals: {
    url: string;
  };
  blacklistedHospitals: {
    url: string;
  };
  faqs: Array<{
    uiorder: number;
    answer: string;
    question: string;
  }>;
  costTypeLabel: string;
}


export interface IMembership {
  id: string;
  status: string;
  details: {
    inceptionDate: string;
    expirationDate: string;
    policyNumber: string;
    sumInsured: string;
  };
  attributes: Array<{
    label: string;
    key: string;
    value: string;
  }>;
}

export interface IBenefit {
  planVariant: 'saver' | 'standard' | 'premium';
  orgBenefitId: string;
  plan: IPlan;
  membership: IMembership;
  coverageType: 'e' | 'esc' | 'escp';
  benefitId: string;
  planVariantBasedChoice: string;
  features?: IBenefitFeatures;
}

export interface IBenefitFeatureItem {
  key: string;
  description: string;
  title: string;
}

export interface IBenefitFeatures {
  url: string;
  includes: Array<IBenefitFeatureItem>;
  excludes: Array<IBenefitFeatureItem>;
}

export interface IDependent {
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  relationship: EPatientRelationship;
  dependentId: string;
  memberId: string;
  name: string;
  membership: Array<IBenefit>;
}

export interface IMember {
  dob: string;
  memberId: string;
  lastName: string;
  firstName: string;
  gender: 'male' | 'female';
  admin: boolean;
  dependents?: Array<IDependent>;
  eid: string;
  email: string;
  organisationId: string;
  organisationName: string;
  phoneNumber: string;
  relationship: EPatientRelationship;
  name: string;
  benefits: Array<IBenefit>;
  featureCategories: Record<string, IFeatureCategory>;
  dependentId?: string | null;
}

export enum ECareType {
  IPD = 'IPD',
  PRE_POST = 'PrePost',
  DAY_CARE = 'DayCare',
  OPD = 'OPD',
}


export interface ITreatmentStage {
  formSchemaField: 'start_date' | 'end_date';
  slug: string;
  subtitle: string;
  title: string;
  tentativeDaysOptionList: Array<{ text: string; value: number }>;
}

export interface IFeatureCategory {
  benefits: Array<string>;
  text: string;
  subtext: string;
  typeOfCare: ECareType;
  treatmentStageOptionList: Array<ITreatmentStage>;
  formSchema: {
    start_date: {
      text: string;
      field: string;
      type: string;
      lower_limit: string;
      upper_limit: string;
      helper_text?: string;
    };
    end_date: {
      text: string;
      field: string;
      type: string;
      lower_limit: string;
      upper_limit: string;
      helper_text?: string;
    };
  };
}

export type IQuery = {
  queryKey: QueryKey;
  queryFn: QueryFunction;
};

export type INodeId = 'claims' | 'stu' | 'telehealth' | 'policy-gpt' | 'health-checkup';
export enum ENodes {
  claims = 'claims',
  stu = 'stu',
  telehealth = 'telehealth',
  healthCheckup = 'health-checkup',
  healthCard = 'health-card',
  modifySelfDependents = 'modify-self-dependents',
}

export enum ETransactionTypes {
  TELEHEALTH = 'TELEHEALTH',
  CLAIMS = 'CLAIMS',
  STU = 'STU',
}

export enum EClaimType {
  REIMBURSEMENT = 'Reimbursement',
  EMERGENCY = 'Emergency',
  PLANNED = 'Planned',
  CASHLESS = 'Cashless',
  ALL = 'All',
}

// Mirror of apps/chat-framework/src/types/index.ts EBotType - keep in sync.
export enum EBotType {
  CASHLESS_GPT_V2 = 'cashlessgptv2',
  CLAIMS_BOT = 'claims_bot',
  REIMBURSEMENT_CLAIMS_BOT = 'reimbursement-claims-bot',
  HEALTH_ID_BOT = 'health_id_bot',
  MANAGE_DEPENDENT_BOT = 'manage_dependent_bot',
  CASHLESS_SUPPORT_BOT = 'cashless_support_bot',
}

export type IChatTypes = 'TRANSACTIONAL' | 'POLICYGPT' | 'AGENT';

export type INode = {
  nodeId: INodeId;
  title: string;
  description: string;
  nodeType: NodeTypeEnum;
  thumbIconUrl: string;
  isLeaf: boolean;
  metaContent: {
    txnType: string;
    articleId?: string;
    collectionName?: string;
    botId?: string;
    chatType?: IChatTypes;
    path: string;
    mixpanelArticleId: string;
    mixpanelCategory: string;
    mixpanelNodeId: string;
    mixpanelSubcategory: string;
    actions?:Array<Record<string,string>>;
  };
  nodes: string[];
};

export type IHomeQuery = {
  homePageData: {
    sos: {
      isEnabled: boolean;
      title: string;
      supportContactNumber: string;
    };
    node: {
      nodeId: INodeId;
      nodeType: NodeTypeEnum;
      nodes: INode[];
    };
  };
};

export enum ETicket{
  IS_MY_TREATMENT_COVERED_IMTC_AT = 'is_my_treatment_covered_imtc-at'
}

export enum EArticletCta{
  BOT_FRAMEWORK='BOT_FRAMEWORK',
  DEEPLINK='DEEPLINK',
  RAISE_TICKET='RAISE_TICKET'
}


export enum NodeTypeEnum {
  TXN_VIEW_PLUS_CT = 'TXN_VIEW_PLUS_CT',
  CATEGORY_TREE_VIEW = 'CATEGORY_TREE_VIEW',
  CHAT_VIEW = 'CHAT_VIEW',
  ARTICLE_VIEW = 'ARTICLE_VIEW',
  DEEPLINK = 'DEEPLINK',
  WEBVIEW = 'WEBVIEW',
  TICKET_FORM_VIEW = 'TICKET_FORM_VIEW',
  ACCORDION = 'ACCORDION',
}

export type ITelehealthAppointment = {
  id: string;
  patientName: string;
  statusBadge: ETelehealthAppointmentStatus;
  consultant: {
    firstName: string;
    lastName?: string;
    imageUrl: string;
    specialities: string;
    consultationRefNum:string;
    gender:string;
    experienceInYears:string;
  };
  isPrescriptionAvailable:boolean;
  state:string;
  status:string;
  patientRelation:string;
  patientGender:string;
  rating:{
    rating: number | string;
    count: number | string;
  }
  feedback:{
    comment: string;
    consultantRating:string;
    videoExpRating:string;
  }
  isCallStarted:boolean;
  isDoctorJoined:boolean;
  isPatientJoined:boolean;
  slot: {
    slotDateTime: string;
    endTime:string;
  };
  reasonForConsultation:string;
  startOfPracticeYear: string | number;
};

export type ISuperTopupPolicy = {
  sumInsured: number;
  policyId: string;
  policyType?: 'INDIVIDUAL' | 'FAMILYFLOATER';
  id: string;
  proposedMembers: ISuperTopupProposedMember[];
};

export type IMetaContent = {
  txnType: string;
  appointments?: ITelehealthAppointment[];
  policies?: ISuperTopupPolicy[];
  claims?: IClaim[];
  healthCheckupBookings?: IHealthCheckupBooking[];
  path: string;
  mixpanelArticleId: string;
  mixpanelCategory: string;
  mixpanelNodeId: string;
  mixpanelSubcategory: string;
};

export type ITransactionViewPlusCTQuery = {
  transactionPlusCTData: {
    node: {
      nodeId: INodeId;
      nodeType: NodeTypeEnum;
      title: string;
      description: string;
      isLeaf: boolean;
      metaContent: IMetaContent;
      nodes: INode[];
      subnodesHeader: string;
    };
  };
};

export type IArticleMediaType =
  | 'image/webp'
  | 'image/png'
  | 'image/jpg'
  | 'image/jpeg'
  | 'video/mp4';

export type IArticleMedia = {
  data: Array<{
    id: string;
    attributes: {
      name: string;
      caption: string;
      width: string;
      height: string;
      url: string;
      size: number;
      previewUrl: string;
      mime: IArticleMediaType;
    };
  }>;
};

export type IArticleQuery = {
  articleData: {
    data: {
      id: string;
      meta: unknown;
      attributes: {
        title: string;
        body1: string;
        body2?: string;
        body3?: string;
        media1: IArticleMedia;
        media2?: IArticleMedia;
        media3?: IArticleMedia;
        contentLengthDescription?: string;
      };
    };
  };
};

export type ICategoryTreeQuery = {
  categoryTreeData: {
    node: {
      title: string;
      metaContent: IMetaContent;
      nodes: INode[];
    };
  };
};

export type IClaimTypeOfCare =
  | 'OPD'
  | 'Home Care'
  | 'DayCare'
  | 'IPD'
  | 'PrePost';

export type IClaimStatus =
  | 'IN PROCESS'
  | 'APPROVED'
  | 'REJECTED'
  | 'PAUSED'
  | 'NOT APPLICABLE'
  | 'IN DRAFTS'
  | 'REQUEST RECEIVED';

export type ITypeOfClaim =
  | 'Emergency'
  | 'Reimbursement'
  | 'Planned'
  | 'Cashless'
  | 'All';

export type IClaim = {
  disputeReasonType?:string;
  investigationReason?:string;
  investigationReasonType?:string;
  unFormattedQuery?:string;
  disputeReason?:string;
  insurerClaimId?:string;
  expected?:unknown;
  insurerRejectionReasonType?:string;
  typeOfClaim: ITypeOfClaim;
  plumClaimId: string;
  typeOfCare: IClaimTypeOfCare | EClaimType;
  memberName: string;
  memberId: string;
  patientRelationship: string;
  patientName: string;
  createdAt: string;
  uiRepresentation: {
    typeOfCare: {
      value: string;
    };
    typeOfClaim: {
      value: string;
    };
    status: {
      colorBackground: any;
      valueCaps: IClaimStatus;
    };
  };
  amount: string;
  amountApproved: string;
  stage: string;
  insurerRejectionReason: string;
  nextExpectedStageDate: string | Date;
  nextExpectedStage: string | Date;
  queryRaisedCounter: number;
  reasonForHospitalization: string;
  deductionReasons: string;
  dateOfAdmission: string;
  dateOfDischarge: string;
  currentStageDate: string;
  reviewDetails?: {
    DURFeedback: Array<{ categoryNotes: Record<string, string> }>;
  };
  fullfilled?: {
    [key: string]: {
      date: string;
    };
  };
  settlementLetterURL?: string;
  approvedLetterURL?: string;
  insuranceProviderHandle?: string;
  typeOfBenefit?: string;
  tpaName?: string;
  modeOfSubmission?: string;
  childClaimRelationList: Array<string>;
  note?: string;
  courierDate?: string;
  courierTrackingNo?: string;
  courierProvider?: string;
  parentPlumClaimId?: string;
  parentRelationReason?: string;
  opsReviewType?: string;
  noteSourceText?: string;
};

export interface ITransactionQuery {
  transactionData: {
    results?: IClaim[];
    appointments?: ITelehealthAppointment[];
  };
}

export interface IClaimsTransactionsQuery {
  page: number;
  results: IClaim[];
  _links: {
    next: string | null;
    prev: string | null;
    self: string | null;
  };
}

export interface ITelehealthTransactionsQuery {
  appointments: ITelehealthAppointment[];
  cursor?: {
    page: number | null;
    limit: number | null;
    nextPage: number | null;
  };
}

export interface ITicket {
  created_at: string;
  updated_at: string;
  id: string;
  description: string;
  subject: string;
  status: string;
}
export interface IAttachment {
  id: string;
  content_url: string;
  file_name: string;
}
export interface IComment {
  created_at: string;
  userType: string;
  body: string;
  plain_body?: string;
  attachments?: IAttachment[];
  html_body?:string;
}

export interface ITicketData {
  commentDetails: { comments: IComment[] };
  ticketDetails?: ITicket;
}

export interface ITicketDataQuery {
  comments?: IComment[];
  ticketDetails?: ITicket;
  duration?: string;
  activeTicketText?: string;
}

export type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U];

export enum ETelehealthAppointmentStatus {
  WAITING_FOR_DOCTOR = 'WAITING FOR DOCTOR',
  ONGOING = 'ONGOING',
  WAITING_FOR_PATIENT = 'WAITING FOR PATIENT',
  WAITING_FOR_PRESCRIPTION = 'WAITING FOR PRESCRIPTION',
  CANCELLED = 'CANCELLED',
  APPOINTMENT_FAILED = 'APPOINTMENT FAILED',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
}

export interface WindowWithWebviewMethods extends Window {
  handleLogin: (token: string) => void;
  NativeBridge: {
    onNativeClose: () => void;
    onNativeError: () => void;
    onNativeCallback: () => void;
    callDeeplink: (url: string) => void;
    onLoadComplete: () => void;
    callAnalytics: (eventName: string, data: string) => void;
    onNativeLogout: () => void;
  };
}

export const ETicketStatus = {
  ACTIVE: 'ACTIVE',
  CLOSED: 'CLOSED',
};

export enum EMessageType {
  TOKEN = 'TOKEN',
  READY = 'READY',
  // CB = CLAIMS_BOT
  CB_CLAIM_DETAILS = 'CB_CLAIM_DETAILS',
  CB_INITIAL_PROMPT = 'CB_INITIAL_PROMPT',
  CB_CLEAR_CHAT = 'CB_CLEAR_CHAT',
  CLOSE_IFRAME = 'CLOSE_IFRAME',
  // NB = NativeBridge
  NB_CALLBACK = 'NB_CALLBACK',
  OPEN_ARTICLE = 'OPEN_ARTICLE',
  LOG_OUT = 'LOG_OUT'
}

export interface IMessagePayload {
  type: EMessageType;
  data: Array<EMessageType> | string | object | boolean;
}

export enum EHealthCheckupBookingStatus {
  SCHEDULED = 'SCHEDULED',
  PARTNER_ASSIGNED = 'PARTNER_ASSIGNED',
  SAMPLE_COLLECTED = 'SAMPLE_COLLECTED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  REPORTS_AVAILABLE = 'REPORTS_AVAILABLE',
  RESCHEDULED = 'RESCHEDULED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type IHealthCheckupBooking = {
  bookingId: string;
  customerBookingId: string;
  statusDetail: {
    enumVal: EHealthCheckupBookingStatus;
    value: string;
    text: string;
    description: string;
    color: string;
  };
  packageDetails: {
    id: string;
    name: string;
    icon: string;
  };
  patientName: string;
  appointmentDateTimeDetails: {
    fullDate: string;
    epochTime: number;
    weekDay: string;
    month: string;
    date: number;
    year: number;
    fullTime: string;
  };
  orderId: string;
};

export interface IHealthCheckupBookingsApiResponse {
  data: {
    bookingDetails: IHealthCheckupBooking[];
    totalCount: number;
  };
  success: boolean;
  message: string;
}